import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, X, MessageCircle, Trash2, Minus, Plus } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useSite } from "@/site/SiteContext";
import { useFavorites } from "@/favorites/FavoritesContext";
import { useQuickView } from "@/components/QuickViewProvider";
import { mediaSrc } from "@/lib/media";

const FavoritesUI = () => {
  const { t } = useLang();
  const { contact } = useSite();
  const { favorites, remove, clear, meta, setMeta, panelOpen, openPanel, closePanel } = useFavorites();
  const { products, open } = useQuickView();

  const favProducts = favorites.map((id) => products.find((p) => p.product_id === id)).filter(Boolean);

  useEffect(() => {
    if (!panelOpen) return;
    const onKey = (e) => e.key === "Escape" && closePanel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [panelOpen, closePanel]);

  const waHref = () => {
    const lines = favProducts.map((p, i) => {
      const m = meta[p.product_id] || {};
      const qty = m.qty || 1;
      const size = (m.size || "").trim();
      let line = `${i + 1}. ${p.title} — ${qty} ${t("favorites.unit")}`;
      if (size) line += ` (${t("favorites.size")}: ${size})`;
      return line;
    });
    return `https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(`${t("favorites.waIntro")}\n\n${lines.join("\n")}`)}`;
  };

  const getQty = (id) => (meta[id]?.qty ?? 1);

  return (
    <>
      <AnimatePresence>
        {favorites.length > 0 && (
          <motion.button
            data-testid="favorites-fab"
            initial={{ opacity: 0, scale: 0.6, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.6, y: 20 }}
            onClick={openPanel}
            aria-label={t("favorites.title")}
            className="fixed bottom-6 left-4 sm:left-6 z-40 flex items-center gap-2 pl-3.5 pr-4 py-3 rounded-full bg-card border border-gold/40 text-gold shadow-lg hover:bg-gold hover:text-black transition-colors"
          >
            <Heart size={20} className="fill-current" />
            <span data-testid="favorites-count" className="font-sans font-semibold text-sm">
              {favorites.length}
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {panelOpen && (
          <motion.div
            data-testid="favorites-panel"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[130] bg-black/80 backdrop-blur-sm flex justify-end"
            onClick={closePanel}
          >
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md h-full bg-card border-l border-gold/20 flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b border-border">
                <div className="flex items-center gap-2">
                  <Heart size={18} className="text-gold fill-current" />
                  <h3 className="font-serif text-xl text-foreground">{t("favorites.title")}</h3>
                  <span className="text-sm text-muted-foreground">({favProducts.length})</span>
                </div>
                <button
                  data-testid="favorites-close"
                  onClick={closePanel}
                  aria-label={t("legal.back")}
                  className="flex items-center justify-center w-9 h-9 rounded-full bg-black/40 text-white hover:bg-gold hover:text-black transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-3">
                {favProducts.length === 0 ? (
                  <p data-testid="favorites-empty" className="text-sm text-muted-foreground font-sans leading-relaxed">
                    {t("favorites.empty")}
                  </p>
                ) : (
                  favProducts.map((p) => (
                    <div
                      key={p.product_id}
                      data-testid={`favorite-item-${p.product_id}`}
                      className="rounded-sm border border-border p-3 space-y-3"
                    >
                      <div className="flex gap-3 items-start">
                        <button
                          onClick={() => {
                            closePanel();
                            open(p);
                          }}
                          className="w-16 h-16 bg-muted rounded-sm overflow-hidden shrink-0"
                        >
                          {p.image ? (
                            <img src={mediaSrc(p.image)} alt={p.title} loading="lazy" className="w-full h-full object-cover" />
                          ) : null}
                        </button>
                        <button
                          onClick={() => {
                            closePanel();
                            open(p);
                          }}
                          className="flex-1 min-w-0 text-left"
                        >
                          <span className="text-[10px] uppercase tracking-widest text-gold">
                            {t(`catalog.cats.${p.category}`)}
                          </span>
                          <p className="text-sm text-foreground font-sans line-clamp-2">{p.title}</p>
                        </button>
                        <button
                          data-testid={`favorite-remove-${p.product_id}`}
                          onClick={() => remove(p.product_id)}
                          aria-label={t("favorites.remove")}
                          className="flex items-center justify-center w-8 h-8 rounded-full text-muted-foreground hover:text-gold hover:bg-gold/10 transition-colors shrink-0"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground font-sans mr-1">{t("favorites.qty")}</span>
                          <button
                            data-testid={`favorite-qty-minus-${p.product_id}`}
                            onClick={() => setMeta(p.product_id, { qty: Math.max(1, getQty(p.product_id) - 1) })}
                            aria-label="-"
                            className="flex items-center justify-center w-7 h-7 rounded-full border border-border text-foreground hover:border-gold hover:text-gold transition-colors"
                          >
                            <Minus size={13} />
                          </button>
                          <span data-testid={`favorite-qty-${p.product_id}`} className="w-7 text-center text-sm text-foreground font-sans">
                            {getQty(p.product_id)}
                          </span>
                          <button
                            data-testid={`favorite-qty-plus-${p.product_id}`}
                            onClick={() => setMeta(p.product_id, { qty: getQty(p.product_id) + 1 })}
                            aria-label="+"
                            className="flex items-center justify-center w-7 h-7 rounded-full border border-border text-foreground hover:border-gold hover:text-gold transition-colors"
                          >
                            <Plus size={13} />
                          </button>
                        </div>
                        <input
                          data-testid={`favorite-size-${p.product_id}`}
                          value={meta[p.product_id]?.size || ""}
                          onChange={(e) => setMeta(p.product_id, { size: e.target.value })}
                          placeholder={t("favorites.sizePlaceholder")}
                          className="flex-1 min-w-0 bg-background border border-border focus:border-gold rounded-sm px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground outline-none transition-colors font-sans"
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>

              {favProducts.length > 0 && (
                <div className="p-6 border-t border-border flex flex-col gap-3">
                  <a
                    data-testid="favorites-whatsapp"
                    href={waHref()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-gold text-black font-sans font-semibold text-sm uppercase tracking-[0.12em] px-6 py-3.5 rounded-sm hover:bg-gold-deep transition-colors"
                  >
                    <MessageCircle size={17} /> {t("favorites.sendWa")}
                  </a>
                  <button
                    data-testid="favorites-clear"
                    onClick={clear}
                    className="inline-flex items-center justify-center gap-2 text-xs font-sans uppercase tracking-[0.12em] text-muted-foreground hover:text-gold transition-colors"
                  >
                    <Trash2 size={14} /> {t("favorites.clear")}
                  </button>
                </div>
              )}
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FavoritesUI;
